import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";

import { getContratosDetalle } from "@/lib/api";
import { formatDate } from "@/lib/utils";

type Props = {
  open: boolean;
  anio: number;
  emisor: string | null;
  adquiriente: string | null;
  estatus?: string[];
  onClose: () => void;
};

export function ContratosDetalleModal({
  open,
  anio,
  emisor,
  adquiriente,
  estatus,
  onClose,
}: Props) {
  const detalle = useQuery({
    queryKey: ["contratos-detalle", anio, emisor, adquiriente, estatus],
    queryFn: () =>
      getContratosDetalle({
        anio,
        emisor,
        adquiriente,
        estatus,
        limit: 500,
      }),
    enabled: open,
  });

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <div>
            <h2 className="font-semibold">Contratos del año {anio}</h2>
            <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-3">
              <span><strong>Emisor:</strong> {emisor ?? "—"}</span>
              <span><strong>Adquiriente:</strong> {adquiriente ?? "—"}</span>
              {estatus && estatus.length > 0 && (
                <span><strong>Estatus:</strong> {estatus.join(", ")}</span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-900"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto">
          {detalle.isLoading && (
            <div className="px-5 py-8 text-center text-slate-500">Cargando…</div>
          )}
          {detalle.isError && (
            <div className="px-5 py-8 text-center text-red-600">
              Error: {(detalle.error as Error).message}
            </div>
          )}
          {detalle.data && (
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-600 text-left sticky top-0">
                <tr>
                  <th className="px-3 py-2 font-medium">Folio</th>
                  <th className="px-3 py-2 font-medium">Tipo</th>
                  <th className="px-3 py-2 font-medium">Estatus</th>
                  <th className="px-3 py-2 font-medium">Fecha firma</th>
                  <th className="px-3 py-2 font-medium">Inicio</th>
                  <th className="px-3 py-2 font-medium">Fin</th>
                </tr>
              </thead>
              <tbody>
                {detalle.data.items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-8 text-center text-slate-500">
                      Sin contratos para los filtros.
                    </td>
                  </tr>
                )}
                {detalle.data.items.map((c) => (
                  <tr key={c.id_contrato} className="border-t hover:bg-slate-50">
                    <td className="px-3 py-1.5 font-mono text-xs">{c.folio ?? "—"}</td>
                    <td className="px-3 py-1.5">{c.tipo_contrato ?? "—"}</td>
                    <td className="px-3 py-1.5">
                      <EstatusBadge estatus={c.estatus} />
                    </td>
                    <td className="px-3 py-1.5">{formatDate(c.fecha_firma)}</td>
                    <td className="px-3 py-1.5">{formatDate(c.fecha_inicio)}</td>
                    <td className="px-3 py-1.5">{formatDate(c.fecha_fin)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-5 py-3 border-t flex items-center justify-between">
          <div className="text-sm text-slate-500">
            {detalle.data ? `${detalle.data.items.length} de ${detalle.data.total}` : ""}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="border rounded px-4 py-1.5 text-sm hover:bg-slate-50"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

export function EstatusBadge({ estatus }: { estatus: string | null }) {
  if (!estatus) return <span className="text-slate-400">—</span>;
  const cls = ESTATUS_COLOR[estatus] ?? "bg-slate-100 text-slate-700";
  return (
    <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${cls}`}>
      {estatus}
    </span>
  );
}

export const ESTATUS_COLOR: Record<string, string> = {
  ACE: "bg-emerald-100 text-emerald-700",
  CSA: "bg-amber-100 text-amber-700",
  TER: "bg-slate-200 text-slate-700",
};

/** Color sólido (hex) para gráficas Gantt. */
export const ESTATUS_HEX: Record<string, string> = {
  ACE: "#10b981",
  CSA: "#f59e0b",
  TER: "#94a3b8",
};
